/**
 * File audio thu sẵn cho câu nhắc — Prompt.md §19, §20.
 *
 * Vì sao có tầng này thay vì chỉ dùng Web Speech: TTS trên máy thật không đáng tin. Đo trên Android
 * của người dùng, câu nhắc phát ra nhưng **đứt giữa câu**; trên iOS thì `speak()` có thể nhận
 * utterance rồi không bắn sự kiện nào. Một file mp3 thì phát đủ hay không phát, không có trạng thái
 * "phát một nửa" phụ thuộc engine giọng nói của từng máy.
 *
 * Thêm một điểm chỉ file mới làm được, quan trọng trên iOS: `<audio>` chạy trong audio session
 * "playback" nên **không bị nút gạt tắt tiếng bên cạnh máy chặn**, còn chime WebAudio thì bị. Nút đó
 * là nghi phạm số một của mọi báo cáo "không nghe thấy gì" và web không có cách nào đọc trạng thái
 * của nó.
 *
 * TTS vẫn giữ làm đường lùi cuối (xem `WebAlertProvider.speak`): thiếu file thì vẫn phải nhắc.
 */

import { alertMessages } from "@studystraight/shared-config";
import type { AlertMessageKey } from "@studystraight/shared-types";

/**
 * Thư mục file, đi qua `BASE_URL` chứ không hardcode `/audios/`.
 *
 * Hôm nay `base` là `/` nên hai cách giống nhau. Nhưng nếu deploy dưới một sub-path, đường dẫn tuyệt
 * đối sẽ 404 → mọi câu rơi hết về `generic.mp3` mà **không có lỗi nào hiện ra**: trẻ vẫn nghe một
 * câu hợp lý, không ai biết là app đã mất 5/6 câu nhắc.
 */
const CLIP_DIR = `${import.meta.env.BASE_URL}audios/`;

/** Câu chung, dùng khi file riêng của issue chưa được thu. */
const FALLBACK_KEY: AlertMessageKey = "GENERIC";

/**
 * Key **không** được rơi về câu chung.
 *
 * Bốn issue tư thế đều là "chỉnh lại người", nên `generic.mp3` ("Con điều chỉnh lại tư thế nhé.")
 * thay cho chúng thì chỉ chung hơn, không sai. `EYE_BREAK` thì khác loại: ý là *nghỉ mắt, nhìn ra
 * xa*. Phát câu chỉnh tư thế vào đúng lúc đó là nói sai với một đứa trẻ đang tin lời app — thà
 * xuống TTS đọc đúng câu, hoặc im, còn hơn.
 */
const NO_FALLBACK: readonly AlertMessageKey[] = ["EYE_BREAK"];

/** Trần chờ một clip phát xong khi không đọc được `duration` (stream lỗi, metadata thiếu). */
const CLIP_TIMEOUT_MS = 15_000;

/**
 * Danh sách key suy ra từ `alertMessages` chứ không viết tay.
 *
 * `alertMessages` là `Record<AlertMessageKey, string>` nên nó *là* tập đóng của các câu. Viết tay
 * danh sách thứ hai nghĩa là thêm một câu nhắc mới thì phải nhớ sửa hai chỗ — và chỗ bị quên sẽ là
 * chỗ này, vì thiếu file chỉ làm câu đó rơi về `generic.mp3` chứ không làm gì đổ vỡ.
 */
const CLIP_KEYS = Object.keys(alertMessages) as readonly AlertMessageKey[];

/** `HEAD_DOWN` → `head-down.mp3`. Quy ước đủ chặt để không cần bảng ánh xạ nào lệch được. */
function clipUrl(key: AlertMessageKey): string {
  return `${CLIP_DIR}${key.toLowerCase().replace(/_/g, "-")}.mp3`;
}

const elements = new Map<AlertMessageKey, HTMLAudioElement>();

/**
 * Key đã xác định là **không có file** — thiếu thật, hoặc không decode được.
 *
 * Nhớ lại để không request một URL 404 mỗi lần nhắc. Chỉ ghi vào đây khi lỗi thuộc loại "không tải
 * được media"; `NotAllowedError` (autoplay bị chặn) **không** vào đây vì nó hết sau cú bấm đầu tiên
 * — nhớ nó vĩnh viễn là tự tắt audio cả phiên vì một lần bị chặn.
 */
const unavailable = new Set<AlertMessageKey>();

function element(key: AlertMessageKey): HTMLAudioElement {
  const existing = elements.get(key);
  if (existing) return existing;

  const audio = new Audio(clipUrl(key));
  // `preload="auto"`: file dưới 32 KB, tải sẵn lúc mở màn Setup để cảnh báo đầu tiên không phải chờ
  // mạng. Service worker precache chúng nên từ lần thứ hai là đọc từ cache.
  audio.preload = "auto";
  elements.set(key, audio);
  return audio;
}

/** Lỗi của `play()` thuộc loại "file không dùng được" (nhớ được) hay chỉ là tạm thời? */
function isMissingMedia(err: unknown): boolean {
  if (!(err instanceof DOMException)) return false;
  return err.name === "NotSupportedError" || err.name === "NotFoundError";
}

/** Chờ clip phát xong. `false` = lỗi giữa đường, phía gọi còn kịp hạ xuống đường khác. */
function waitForEnd(audio: HTMLAudioElement): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const settle = (value: boolean): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      audio.removeEventListener("ended", onEnd);
      audio.removeEventListener("error", onError);
      resolve(value);
    };
    const onEnd = (): void => settle(true);
    const onError = (): void => settle(false);

    // Có mốc chờ vì `ended` không được bảo đảm bắn: buffer hụt giữa clip là đủ để nó im lặng đứng
    // lại, và `announce()` đang await ở đây.
    const limit = Number.isFinite(audio.duration)
      ? audio.duration * 1000 + 1500
      : CLIP_TIMEOUT_MS;
    const timer = setTimeout(() => settle(true), limit);

    audio.addEventListener("ended", onEnd);
    audio.addEventListener("error", onError);
  });
}

async function playOne(key: AlertMessageKey): Promise<boolean> {
  if (unavailable.has(key)) return false;

  const audio = element(key);
  try {
    // Về đầu chứ không `new Audio()` mỗi lần: dùng lại element đã decode, và mỗi element mới trên
    // iOS lại là một element chưa được mở khoá trong user gesture.
    audio.pause();
    audio.currentTime = 0;
    audio.muted = false;
    await audio.play();
  } catch (err) {
    if (isMissingMedia(err)) unavailable.add(key);
    return false;
  }
  return waitForEnd(audio);
}

export type AlertClipResult =
  /** Đã phát đúng câu của issue này. */
  | "played"
  /** Không có file riêng → đã phát `generic.mp3`. Vẫn là một câu nhắc đúng, chỉ chung hơn. */
  | "fallback"
  /** Không phát được gì — phía gọi phải hạ xuống TTS. */
  | "unavailable";

/**
 * Phát câu nhắc của `key`, thiếu file thì phát câu chung.
 *
 * Thứ tự này chứ không phải TTS-trước-file: file thu sẵn là đường đáng tin nhất (xem chú thích đầu
 * file), TTS chỉ để không bao giờ im lặng hoàn toàn.
 */
export async function playAlertClip(key: AlertMessageKey): Promise<AlertClipResult> {
  if (await playOne(key)) return "played";
  if (key === FALLBACK_KEY || NO_FALLBACK.includes(key)) return "unavailable";
  return (await playOne(FALLBACK_KEY)) ? "fallback" : "unavailable";
}

/**
 * Mở khoá **đồng bộ** trong user gesture. Gọi cùng chỗ với `unlockAudio()`.
 *
 * iOS xét quyền phát theo **từng element**, không theo cả trang: một element chưa từng `play()`
 * trong gesture thì sau đó gọi `play()` sẽ bị chặn. Nên phải chạm vào cả 6, không chỉ một.
 *
 * `muted = true` rồi `pause()` ngay: đủ để element được đánh dấu đã-mở-khoá mà không phát ra tiếng
 * nào giữa màn Setup. (`volume = 0` không dùng được — iOS bỏ qua `volume` của media element.)
 *
 * Chưa kiểm được trên iPhone: máy test hiện có không chạy nổi app vì WebGL chết ở tầng thiết bị.
 * Trên Android thì `play()` sau gesture không bị chặn nên hàm này là dư — nó tồn tại cho iOS.
 */
export function primeAlertClips(): void {
  for (const key of CLIP_KEYS) {
    const audio = element(key);
    audio.muted = true;
    void audio
      .play()
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
      })
      .catch((err: unknown) => {
        // Thiếu file thì biết luôn từ đây, khỏi phải chờ tới lúc nhắc mới phát hiện.
        if (isMissingMedia(err)) unavailable.add(key);
      })
      .finally(() => {
        audio.muted = false;
      });
  }
}
